import { ref, onBeforeUnmount } from 'vue';
import { CalendarSyncSocketAction } from '@/services/socket/actions/CalendarSyncSocketAction';
import { CalendarEvent } from '@/services/api-providers/CalendarServiceProvider';
import apiService from '@/services/ApiService';
import authService from '@/services/AuthService';

interface CalendarSyncCallbacks {
  onEventUpdated?: (event: CalendarEvent) => void;
  onEventDeleted?: (providerEventId: string) => void;
  onBotStatusChanged?: (payload: { eventId: string; botInviteStatus: string; botInviteError?: string }) => void;
  onSyncCompleted?: () => void;
  onSyncError?: (error: string) => void;
  /** @internal Test-only: inject a mock socket factory */
  _socketFactory?: () => CalendarSyncSocketAction;
}

export function useCalendarSync(callbacks: CalendarSyncCallbacks = {}) {
  const lastSyncAt = ref<string | null>(null);
  const isSyncing = ref(false);
  const syncError = ref<string | null>(null);
  const isConnected = ref(false);

  let socket: CalendarSyncSocketAction | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = 5000;
  let unmounted = false;
  const RECONNECT_MAX_DELAY = 30000;
  const RECONNECT_INITIAL_DELAY = 5000;

  const clearReconnectTimer = () => {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (unmounted) return;
    clearReconnectTimer();
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (!unmounted) {
        connect();
      }
    }, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_DELAY);
  };

  const connect = () => {
    if (callbacks._socketFactory) {
      socket = callbacks._socketFactory();
    } else {
      const protocol = window.location.hostname === 'localhost' ? 'ws' : 'wss';
      const origin = (apiService.baseUrl || '').replace(/^https?:\/\//, '');
      let userId = '';
      try {
        userId = authService.user?.id || '';
      } catch {
        /* store not initialized */
      }
      socket = new CalendarSyncSocketAction(`${protocol}://${origin}`, userId);
    }

    socket.onSyncStarted(() => {
      isSyncing.value = true;
      syncError.value = null;
    });

    socket.onSyncCompleted((payload) => {
      isSyncing.value = false;
      lastSyncAt.value = payload.lastSyncAt;
      callbacks.onSyncCompleted?.();
    });

    socket.onSyncError((error) => {
      isSyncing.value = false;
      syncError.value = error;
      callbacks.onSyncError?.(error);
    });

    socket.onEventUpdated((event) => {
      callbacks.onEventUpdated?.(event as CalendarEvent);
    });

    socket.onEventDeleted((providerEventId) => {
      callbacks.onEventDeleted?.(providerEventId);
    });

    socket.onBotStatusChanged((payload) => {
      callbacks.onBotStatusChanged?.(payload);
    });

    socket.onDisconnect(() => {
      isConnected.value = false;
      scheduleReconnect();
    });

    socket.onConnect(() => {
      reconnectDelay = RECONNECT_INITIAL_DELAY;
      isConnected.value = true;
    });
  };

  const disconnect = () => {
    clearReconnectTimer();
    socket?.disconnect();
    socket = null;
    isConnected.value = false;
  };

  onBeforeUnmount(() => {
    unmounted = true;
    disconnect();
  });

  return {
    lastSyncAt,
    isSyncing,
    syncError,
    isConnected,
    connect,
    disconnect,
  };
}
